import memoize from '../../memoize';
import {
  ICPYDatum,
  ILoadable,
  IPYDatum,
  LoadableStatus,
} from '../../Utils';
import {
  ILayoutData,
} from '../fetchedData/layoutData';
import {
  IMetadatum,
} from '../fetchedData/productMetadata';
import getSize from '../network/getSize';
import unmemoizedAddTooltipInfo from './addTooltipInfo';
import unmemoizedFilter from './filterByCategory';
import unmemoizedGetDropdownOptions from './getDropdownOptions';
import unmemoizedGetEdgesLookupTable from './getEdgesLookupTable';
import unmemoizedGetMainDataMap from './getMainDataMap';
import unmemoizedGetNodesLinksLayout from './getNodesLinksLayout';
import {
  IHasNoError,
  IInputFromURLRouting,
  ISuccessfulMergeAction,
  ISuccessfulMergePayload,
} from './getReducer';
import unmemoizedGetTextLayout from './getTextLayout';
import unmemoizedGetTradeValueGetter from './getTradeValueGetter';
import unmemoiedGetUnfilteredTotal from './getUnfilteredTotal';
import {
  updateMergedData,
} from './reducer';

import {
  IComputationOutput,
} from './Utils';

export interface IComputationInput {
  chartContainerHeight: number | undefined;
  chartContainerWidth: number | undefined;
  layoutData: ILoadable<ILayoutData>;
  centerNode: number;
  metadata: ILoadable<Map<number, IMetadatum>>;
  mainData: ILoadable<ICPYDatum[]>;
  data_PY: ILoadable<Map<string, IPYDatum>>;
  levelMapping: ILoadable<Map<number, number>>;
  deselectedCategories: number[];
}

export interface IComputeInput {
  // Info that's actually used in the computation:
  computationInput: IComputationInput;

  // Pass through to hash function:
  queueNumber: number;
  inputFromURLRouting: IInputFromURLRouting;
}

const getEdgesLookupTable = memoize(unmemoizedGetEdgesLookupTable);
const getNodesLinksLayout = memoize(unmemoizedGetNodesLinksLayout);
const getTextLayout = memoize(unmemoizedGetTextLayout);
const filterByCategory = memoize(unmemoizedFilter);
const getDropdownOptions = memoize(unmemoizedGetDropdownOptions);
const addTooltipInfo = memoize(unmemoizedAddTooltipInfo);
const getMainDataMap = memoize(unmemoizedGetMainDataMap);
const getTradeValueGetter = memoize(unmemoizedGetTradeValueGetter);
const getUnfilteredTotal = memoize(unmemoiedGetUnfilteredTotal);

const compute = (
    {computationInput, inputFromURLRouting, queueNumber}: IComputeInput,
  ): ISuccessfulMergeAction => {

  const {
    chartContainerHeight, chartContainerWidth,
    layoutData: layoutDataStatus, centerNode,
    metadata: metadataStatus,
    levelMapping: levelMappingStatus,
    mainData: mainDataStatus,
    data_PY: data_PY_Status,
    deselectedCategories,
  } = computationInput;

  const {
    country, queryLevel, year, productClass,
    tradeDirection, tradeFlow,
  } = inputFromURLRouting;

  let value: ILoadable<IComputationOutput>;
  const sizeStatus = getSize(chartContainerWidth, chartContainerHeight);
  if (sizeStatus.status === LoadableStatus.Initial ||
        layoutDataStatus.status === LoadableStatus.Initial ||
        metadataStatus.status === LoadableStatus.Initial ||
        levelMappingStatus.status === LoadableStatus.Initial ||
        mainDataStatus.status === LoadableStatus.Initial ||
        data_PY_Status.status === LoadableStatus.Initial) {
    value = {status: LoadableStatus.Initial};
  } else if (sizeStatus.status === LoadableStatus.Loading ||
              layoutDataStatus.status === LoadableStatus.Loading ||
              metadataStatus.status === LoadableStatus.Loading ||
              levelMappingStatus.status === LoadableStatus.Loading ||
              mainDataStatus.status === LoadableStatus.Loading ||
              data_PY_Status.status === LoadableStatus.Loading) {

    value = {status: LoadableStatus.Loading};
  } else if (sizeStatus.status === LoadableStatus.NotPresent ||
              layoutDataStatus.status === LoadableStatus.NotPresent ||
              metadataStatus.status === LoadableStatus.NotPresent ||
              levelMappingStatus.status === LoadableStatus.NotPresent ||
              mainDataStatus.status === LoadableStatus.NotPresent ||
              data_PY_Status.status === LoadableStatus.NotPresent) {

    value = {status: LoadableStatus.NotPresent};
  } else {
    const {data: {width, height}} = sizeStatus;
    const {data: layoutData} = layoutDataStatus;
    const {data: metadata} = metadataStatus;
    const {data: levelMapping} = levelMappingStatus;
    const {data: mainData} = mainDataStatus;
    const {data: data_PY} = data_PY_Status;

    const lesserDimension = (width < height) ? width : height;
    const chartRadius = lesserDimension / 2 * 0.8;

    const lookupTable = getEdgesLookupTable(layoutData.edges);
    const nodesAndLinks = getNodesLinksLayout(centerNode, chartRadius, lookupTable);
    const withText = getTextLayout(
      productClass, lesserDimension / 2, nodesAndLinks, metadata, levelMapping,
    );
    const filtered = filterByCategory(deselectedCategories, withText, metadata);
    const getTradeValue = getTradeValueGetter(tradeDirection, tradeFlow);
    const dropdownOptions = getDropdownOptions(productClass, metadata, layoutData.edges);
    const mainDataMap = getMainDataMap(mainData);
    const {tooltipMap, total, nodes, texts} = addTooltipInfo(
      filtered.nodes, filtered.texts, mainDataMap, metadata, data_PY,
      tradeDirection, tradeFlow, productClass, year, getTradeValue,
    );
    const unfilteredTotal = getUnfilteredTotal(mainData, getTradeValue);
    value = {
      status: LoadableStatus.Present,
      data: {
        ...filtered,
        dropdownOptions,
        tooltipMap, total,
        width, height,
        nodes, texts,
        unfilteredTotal,
       },
    };
  }
  const mergedData: IHasNoError = {
    hasError: false, value,
  };

  const payload: ISuccessfulMergePayload = {
    hashInput: {
      country, queryLevel, year, chartContainerHeight, chartContainerWidth,
      productClass, centerNode, deselectedCategories,
      tradeDirection, tradeFlow,
    },
    mergedData,
    queueNumber,
  };

  const result: ISuccessfulMergeAction = updateMergedData(payload);
  return result;
};

export default compute;
